/** 交換所の組み立て（㊶ 掲示の一覧、㊷a 交換する、㊸ 状態）。サーバー由来の文字は textContent だけで入れる */
import type { GameState, MyListing } from '../../api/types';
import { fmtAgo, serverNow } from '../../core/clock';
import { btn, h, swapIcon } from './dom';
import { type AreaInfo, anyTitle, areaMap, listingWaitMs, type PairView, sortListings } from './logic';
import type { Ticker } from './ticker';

export interface ExchangeEnv {
  state: GameState;
  ticker: Ticker;
  /** 申し出た直後の掲示（一覧から消える前の見せ方を保つ）。listing_id → 掲示の写し */
  offered: Map<number, PairView>;
  /** 開いている（交換するボタンが出ている）掲示 */
  openId: number | null;
  /** エリアのどれでもの掲示で選んだ渡す写真 */
  picks: Map<number, number>;
  /** 自分の掲示に他チームが応じた直後の見せ方（㊸①） */
  settled: { listing: MyListing; receivedId: number } | null;
  /** 切手と消印の動きを出す手元の写真 */
  fresh: Set<number>;
  onBack(): void;
  onToggle(listingId: number): void;
  onPick(listingId: number, promptId: number): void;
  /** 交換する。失敗したら短い文を返す */
  onOffer(pv: PairView, give: number): Promise<string | null>;
  onWithdraw(): Promise<string | null>;
  onOpenPost(): void;
  onCooldownEnd(): void;
}

interface MiniOpts {
  title: string;
  area: AreaInfo | undefined;
  owned: boolean;
  hot?: boolean;
  isNew?: boolean;
  label?: string;
  /** 題名の下に出すエリア名を消す（エリアのどれでも） */
  hideArea?: boolean;
}

/** 絵葉書の小さな見本。owned＝切手と消印つきの実線の紙、そうでなければ切手の枠が空いた点線（帖と同じ約束事） */
export function mini(o: MiniOpts): HTMLElement {
  const cls = ['pr-mini'];
  if (!o.owned) cls.push('pr-want');
  if (o.hot) cls.push('pr-hot');
  if (o.isNew) cls.push('pr-new');
  const m = h('div', cls.join(' '));
  if (o.area) m.style.setProperty('--c', o.area.hex);
  else m.style.setProperty('--c', '#b9a27a');
  if (o.owned && o.area) m.style.setProperty('--s', `var(--pr-st-${o.area.paint})`);
  if (o.label) m.setAttribute('aria-label', o.label);
  m.appendChild(h('b', undefined, o.title || '　'));
  m.appendChild(h('span', 'pr-ms'));
  if (o.owned) m.appendChild(h('span', 'pr-mp'));
  m.appendChild(h('i', undefined, o.hideArea || !o.area ? '' : o.area.name));
  return m;
}

function pairRow(left: HTMLElement, right: HTMLElement): HTMLElement {
  const pp = h('div', 'pr-pp');
  pp.appendChild(left);
  pp.appendChild(swapIcon());
  pp.appendChild(right);
  return pp;
}

function howRow(): HTMLElement {
  const how = h('div', 'pr-how');
  how.appendChild(h('span', undefined, 'もらう'));
  how.appendChild(swapIcon());
  how.appendChild(h('span', undefined, '渡す'));
  return how;
}

export function buildExchange(env: ExchangeEnv): HTMLElement {
  const s = env.state;
  const areas = areaMap(s);
  const root = h('div', 'pr-xv');
  root.appendChild(btn('pr-back', 'エリア選択に戻る', env.onBack));

  const pairs = sortListings(s.exchange.listings, s);
  for (const pv of env.offered.values()) {
    if (!pairs.some((p) => p.listing.listing_id === pv.listing.listing_id)) pairs.unshift(pv);
  }

  const exh = h('div', 'pr-exh');
  exh.appendChild(h('b', undefined, '交換所'));
  exh.appendChild(h('span', undefined, `掲示 ${pairs.length}件`));
  root.appendChild(exh);

  root.appendChild(buildMine(env, areas));

  root.appendChild(h('h3', 'pr-bh', '掲示一覧'));
  root.appendChild(howRow());
  if (pairs.length === 0) root.appendChild(h('p', 'pr-empty', 'いま出ている掲示はありません。'));
  for (const pv of pairs) root.appendChild(buildPair(pv, env, areas));
  return root;
}

// ---------------------------------------------------------------- 自分の掲示

function buildMine(env: ExchangeEnv, areas: Map<string, AreaInfo>): HTMLElement {
  const s = env.state;
  const box = h('div', 'pr-mybox');
  const wait = listingWaitMs(s.exchange.next_listing_at, serverNow());

  const cooldownButton = (): HTMLElement => {
    const b = h('button', 'pr-btn pr-pri pr-post pr-cd', undefined, { type: 'button', 'aria-disabled': 'true' });
    b.appendChild(document.createTextNode('あと'));
    const t = h('b', 'pr-num');
    b.appendChild(t);
    if (s.exchange.next_listing_at) env.ticker.add(t, s.exchange.next_listing_at, env.onCooldownEnd);
    return b;
  };

  const own = env.settled?.listing ?? s.exchange.my_listing;
  if (own) {
    const settled = env.settled !== null;
    const myl = h('div', 'pr-myl');
    const tgs = h('div', 'pr-tgs');
    tgs.appendChild(h('em', settled ? 'pr-tg pr-me pr-dealt' : 'pr-tg pr-me', settled ? '交換しました' : 'あなたの掲示'));
    tgs.appendChild(h('span', 'pr-tm', settled ? 'いま' : fmtAgo(own.created_at)));
    myl.appendChild(tgs);

    const wantAreaId = own.want_area_id ?? s.prompts.find((p) => p.prompt_id === own.want_prompt_id)?.area_id ?? '';
    const wantArea = areas.get(wantAreaId);
    const wantHeld = own.want_prompt_id !== null && s.prompts.some((p) => p.prompt_id === own.want_prompt_id && p.held !== null);
    const left = mini({
      title: own.want_prompt_id !== null ? (own.want_title ?? '') : anyTitle(wantArea?.name ?? ''),
      area: wantArea,
      owned: settled || wantHeld,
      isNew: settled && env.fresh.has(env.settled?.receivedId ?? -1),
      hideArea: own.want_prompt_id === null,
      label: 'もらう',
    });
    const right = mini({ title: own.title, area: areas.get(own.area_id), owned: true, label: '渡す' });
    myl.appendChild(pairRow(left, right));

    const act = h('div', 'pr-myact');
    const err = h('p', 'pr-err');
    if (settled) {
      act.appendChild(cooldownButton());
    } else {
      const wd = btn('pr-btn pr-sec', '取り下げる');
      wd.addEventListener('click', async () => {
        if (wd.getAttribute('aria-disabled')) return;
        wd.setAttribute('aria-disabled', 'true');
        const e = await env.onWithdraw();
        if (e) {
          err.textContent = e;
          wd.removeAttribute('aria-disabled');
        }
      });
      act.appendChild(wd);
    }
    myl.appendChild(act);
    myl.appendChild(err);
    box.appendChild(myl);
    return box;
  }

  const mine = h('div', 'pr-mine');
  const tx = h('div');
  tx.appendChild(h('b', undefined, '撮った写真を交換できます。'));
  tx.appendChild(h('p', undefined, '出した写真は手元に残ります。'));
  mine.appendChild(tx);
  if (wait > 0) mine.appendChild(cooldownButton());
  else mine.appendChild(btn('pr-btn pr-pri pr-post', '掲示する', env.onOpenPost));
  box.appendChild(mine);
  return box;
}

// ---------------------------------------------------------------- 掲示1件

function buildPair(pv: PairView, env: ExchangeEnv, areas: Map<string, AreaInfo>): HTMLElement {
  const l = pv.listing;
  const done = env.offered.has(l.listing_id);
  const cls = ['pr-pair'];
  if (pv.kind === 'can') cls.push('pr-can');
  if (pv.kind === 'have' && !done) cls.push('pr-own');
  if (done) cls.push('pr-doneoffer');
  if (pv.kind === 'can' && env.openId === l.listing_id && !done) cls.push('pr-open');
  const pair = h('div', cls.join(' '));

  const tgs = h('div', 'pr-tgs');
  if (done) tgs.appendChild(h('em', 'pr-tg pr-can', '交換しました'));
  else if (pv.kind === 'can') tgs.appendChild(h('em', 'pr-tg pr-can', '交換できます'));
  tgs.appendChild(h('span', 'pr-tm', `${l.team_name}・${fmtAgo(l.created_at)}`));
  pair.appendChild(tgs);

  const giveArea = areas.get(l.area_id);
  const wantArea = l.want_area_id ? areas.get(l.want_area_id) : areaOfPrompt(env.state, l.want_prompt_id, areas);
  const left = mini({
    title: l.title,
    area: giveArea,
    owned: pv.giveHeld || done,
    hot: pv.hot,
    isNew: done && env.fresh.has(l.prompt_id),
    label: `もらう ${l.title}`,
  });
  const right = mini({
    title: pv.any ? anyTitle(wantArea?.name ?? '') : (l.want_title ?? ''),
    area: wantArea,
    owned: pv.wantOwned,
    hideArea: pv.any,
    label: `渡す ${pv.any ? anyTitle(wantArea?.name ?? '') : (l.want_title ?? '')}`,
  });
  pair.appendChild(pairRow(left, right));

  if (pv.kind === 'can' && !done) {
    pair.addEventListener('click', (ev) => {
      if ((ev.target as HTMLElement).closest('.pr-inl')) return;
      env.onToggle(l.listing_id);
    });
    pair.appendChild(buildInline(pv, env, areas));
  }
  const sent = h('div', 'pr-sent');
  sent.appendChild(h('span', undefined, '写しがお題選択に届きました'));
  pair.appendChild(sent);
  return pair;
}

function areaOfPrompt(s: GameState, promptId: number | null, areas: Map<string, AreaInfo>): AreaInfo | undefined {
  return areas.get(s.prompts.find((p) => p.prompt_id === promptId)?.area_id ?? '');
}

/** 掲示の下に開く「交換する」（㊷a）。エリアのどれでもの掲示だけ、渡す写真を選ぶ */
function buildInline(pv: PairView, env: ExchangeEnv, _areas: Map<string, AreaInfo>): HTMLElement {
  const l = pv.listing;
  const inl = h('div', 'pr-inl');
  let picked: number | null = null;
  const go = h('button', 'pr-btn pr-pri pr-go', '交換する', { type: 'button' });
  const err = h('p', 'pr-err');
  const first = pv.candidates[0];

  if (pv.any) {
    picked = env.picks.get(l.listing_id) ?? null;
    if (picked !== null && !pv.candidates.some((c) => c.prompt_id === picked)) picked = null;
    const picks = h('div', 'pr-picks');
    for (const c of pv.candidates) {
      const b = h('button', 'pr-pick', c.title, { type: 'button', 'aria-pressed': c.prompt_id === picked ? 'true' : 'false' });
      b.addEventListener('click', () => {
        picked = c.prompt_id;
        env.onPick(l.listing_id, c.prompt_id);
        picks.querySelectorAll('.pr-pick').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
        go.removeAttribute('aria-disabled');
      });
      picks.appendChild(b);
    }
    inl.appendChild(picks);
    if (picked === null) go.setAttribute('aria-disabled', 'true');
  } else {
    picked = first ? first.prompt_id : null;
  }

  go.addEventListener('click', async () => {
    if (go.getAttribute('aria-disabled') || picked === null) return;
    go.setAttribute('aria-disabled', 'true');
    err.textContent = '';
    const e = await env.onOffer(pv, picked);
    if (e) {
      err.textContent = e;
      go.removeAttribute('aria-disabled');
    }
  });
  inl.appendChild(go);
  inl.appendChild(err);
  return inl;
}
